/**
 * Decision Hub — history of the consumer's completed product reviews.
 * Reads local wallet state for the list of journeys the user has interacted
 * with. In a real deployment this would call an authenticated backend.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WalletShell, useWalletActive, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { loadWallet } from "@/lib/wallet-storage";
import { ScrollText, ArrowRight, Sparkles } from "lucide-react";

export default function WalletDecisionHub() {
  const [wallet, setWallet] = useState(null);
  useEffect(() => { setWallet(loadWallet()); }, []);
  const decisions = wallet?.decisions || [];
  const active = useWalletActive();

  return (
    <WalletShell activeTab={active}>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="wallet-decisions">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>My decisions</div>
          <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>Every review you&rsquo;ve completed</h1>
          <p className="text-sm text-slate-600 mt-1">Your decision history stays on this device unless you choose to share it.</p>
        </header>

        {decisions.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 text-center">
            <Sparkles className="w-8 h-8 mx-auto" style={{ color: GOLD }} />
            <div className="mt-3 font-display text-lg font-semibold" style={{ color: NAVY }}>No decisions yet</div>
            <div className="text-sm text-slate-600 mt-1">When your bank sends you a product review, it will appear here.</div>
            {wallet?.last_journey_url && (
              <Link to={new URL(wallet.last_journey_url).pathname} className="inline-flex items-center gap-1.5 mt-4 rounded-xl px-4 py-2 text-sm font-medium text-white" style={{ background: NAVY }}>
                Open your active review <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-slate-100 shadow-sm divide-y divide-slate-100">
            {decisions.map((d, i) => (
              <div key={i} className="p-4 flex items-start gap-3">
                <ScrollText className="w-4 h-4 mt-0.5" style={{ color: GOLD }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold" style={{ color: NAVY }}>{d.product_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{d.institution} · {new Date(d.completed_at).toLocaleDateString()}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-widest inline-block px-1.5 py-0.5 rounded-full font-medium" style={{ background: `${GOLD}22`, color: NAVY }}>
                    {(d.confidence || "").replace(/_/g, " ")}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">score</div>
                  <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>{d.score ?? "—"}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </WalletShell>
  );
}
